/**
 * Schedule Service - Pure business logic for schedule management
 * No API calls, no React hooks - just pure functions for schedule calculations
 */

import type { Task, Event, Supplier } from '../types/mockup'
import type { SchedulingConfig } from '../hooks/useConfig'

export class ScheduleService {
  // Default values - will be overridden by backend config when available
  static MIN_BUFFER_MINUTES = 5
  static DEFAULT_TASK_DURATION = 30
  static ANCHOR_TASK_TYPES = ['ceremony', 'venue-open', 'venue-close']
  
  // Method to update config from backend
  static updateConfig(config: SchedulingConfig) {
    this.MIN_BUFFER_MINUTES = config.min_buffer_minutes
    this.DEFAULT_TASK_DURATION = config.default_task_duration
    this.ANCHOR_TASK_TYPES = config.anchor_task_types
  }
  
  /**
   * Validate if a task can be rescheduled
   */
  static canRescheduleTask(task: Task): { valid: boolean; reason?: string } {
    if (task.isAnchor) {
      return { valid: false, reason: 'Anchor tasks cannot be rescheduled' }
    }
    
    if (task.status === 'complete') {
      return { valid: false, reason: 'Completed tasks cannot be rescheduled' }
    }
    
    return { valid: true }
  }
  
  /**
   * Calculate cascading updates when a task is rescheduled
   */
  static calculateCascadingUpdates(
    task: Task,
    newStartTime: Date,
    allTasks: Task[]
  ): Task[] {
    const updatedTasks: Task[] = []
    const timeDiff = newStartTime.getTime() - new Date(task.startTime).getTime()
    
    // Find all dependent tasks
    const dependentTasks = allTasks.filter(t => 
      t.dependencies?.includes(task.id) && !t.isAnchor
    )
    
    // Update each dependent task
    dependentTasks.forEach(depTask => {
      const newDepStartTime = new Date(
        new Date(depTask.startTime).getTime() + timeDiff
      )
      const newDepEndTime = new Date(
        new Date(depTask.endTime).getTime() + timeDiff
      )
      
      updatedTasks.push({
        ...depTask,
        startTime: newDepStartTime,
        endTime: newDepEndTime
      })
    })
    
    // Add the original task
    const duration = new Date(task.endTime).getTime() - new Date(task.startTime).getTime()
    updatedTasks.push({
      ...task,
      startTime: newStartTime,
      endTime: new Date(newStartTime.getTime() + duration)
    })
    
    return updatedTasks
  }
  
  /**
   * Check for scheduling conflicts
   */
  static findConflicts(tasks: Task[]): Array<{ task1: Task; task2: Task; type: string }> {
    const conflicts: Array<{ task1: Task; task2: Task; type: string }> = []
    
    for (let i = 0; i < tasks.length; i++) {
      for (let j = i + 1; j < tasks.length; j++) {
        const task1 = tasks[i]
        const task2 = tasks[j]
        
        // Check time overlap for same location
        if (task1.location === task2.location) {
          const start1 = new Date(task1.startTime).getTime()
          const end1 = new Date(task1.endTime).getTime()
          const start2 = new Date(task2.startTime).getTime()
          const end2 = new Date(task2.endTime).getTime()
          
          if ((start1 < end2 && end1 > start2)) {
            conflicts.push({
              task1,
              task2,
              type: 'location_overlap'
            })
          }
        }
        
        // Check supplier conflicts
        if (task1.supplierId && task1.supplierId === task2.supplierId) {
          const start1 = new Date(task1.startTime).getTime()
          const end1 = new Date(task1.endTime).getTime()
          const start2 = new Date(task2.startTime).getTime()
          const end2 = new Date(task2.endTime).getTime()
          
          if ((start1 < end2 && end1 > start2)) {
            conflicts.push({
              task1,
              task2,
              type: 'supplier_overlap'
            })
          }
        }
      }
    }
    
    return conflicts
  }
  
  /**
   * Calculate critical path for the event
   */
  static calculateCriticalPath(tasks: Task[]): Task[] {
    // Find tasks with no dependencies (starting points)
    const startTasks = tasks.filter(t => !t.dependencies || t.dependencies.length === 0)
    
    // Build dependency graph and find longest path
    const criticalPath: Task[] = []
    const visited = new Set<string>()
    
    const findLongestPath = (task: Task, currentPath: Task[]): Task[] => {
      if (visited.has(task.id)) return currentPath
      
      visited.add(task.id)
      currentPath.push(task)
      
      // Find tasks that depend on this one
      const dependents = tasks.filter(t => 
        t.dependencies?.includes(task.id)
      )
      
      if (dependents.length === 0) {
        return currentPath
      }
      
      // Find the longest path through dependents
      let longestPath = currentPath
      dependents.forEach(dep => {
        const path = findLongestPath(dep, [...currentPath])
        if (path.length > longestPath.length) {
          longestPath = path
        }
      })
      
      return longestPath
    }
    
    // Find the overall longest path
    startTasks.forEach(start => {
      const path = findLongestPath(start, [])
      if (path.length > criticalPath.length) {
        criticalPath.length = 0
        criticalPath.push(...path)
      }
    })
    
    return criticalPath
  }
  
  /**
   * Calculate buffer time between tasks
   */
  static calculateBufferTime(task1: Task, task2: Task): number {
    const end1 = new Date(task1.endTime).getTime()
    const start2 = new Date(task2.startTime).getTime()
    return (start2 - end1) / (1000 * 60) // Return in minutes
  }
  
  /**
   * Get tasks that need attention (at risk, delayed, etc.)
   */
  static getTasksNeedingAttention(tasks: Task[], currentTime: Date): Task[] {
    return tasks.filter(task => {
      // Already delayed
      if (task.status === 'delayed' || task.status === 'at_risk') {
        return true
      }
      
      // Should have started but hasn't
      if (task.status === 'pending') {
        const startTime = new Date(task.startTime).getTime()
        if (startTime < currentTime.getTime()) {
          return true
        }
      }
      
      // Starting soon (within 15 minutes) but not ready
      if (task.status === 'pending') {
        const startTime = new Date(task.startTime).getTime()
        const fifteenMinutes = 15 * 60 * 1000
        if (startTime - currentTime.getTime() < fifteenMinutes) {
          return true
        }
      }
      
      return false
    })
  }
  
  /**
   * Calculate event progress percentage
   */
  static calculateEventProgress(event: Event): number {
    const totalTasks = event.tasks.length
    if (totalTasks === 0) return 0
    
    const completedTasks = event.tasks.filter(t => t.status === 'complete').length
    return Math.round((completedTasks / totalTasks) * 100)
  }
  
  /**
   * Get supplier workload
   */
  static getSupplierWorkload(supplier: Supplier, tasks: Task[]): {
    totalTasks: number
    completedTasks: number
    currentTasks: Task[]
    upcomingTasks: Task[]
  } {
    const supplierTasks = tasks.filter(t => t.supplierId === supplier.id)
    
    return {
      totalTasks: supplierTasks.length,
      completedTasks: supplierTasks.filter(t => t.status === 'complete').length,
      currentTasks: supplierTasks.filter(t => t.status === 'in_progress'),
      upcomingTasks: supplierTasks.filter(t => t.status === 'pending')
        .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
        .slice(0, 3)
    }
  }
  
  /**
   * Validate schedule integrity
   */
  static validateSchedule(tasks: Task[]): Array<{ type: string; message: string; tasks?: Task[] }> {
    const issues: Array<{ type: string; message: string; tasks?: Task[] }> = []
    
    // Check for conflicts
    const conflicts = this.findConflicts(tasks)
    conflicts.forEach(conflict => {
      issues.push({
        type: 'conflict',
        message: `${conflict.type}: ${conflict.task1.name} and ${conflict.task2.name}`,
        tasks: [conflict.task1, conflict.task2]
      })
    })
    
    // Check for broken dependencies
    tasks.forEach(task => {
      if (task.dependencies) {
        task.dependencies.forEach(depId => {
          const depTask = tasks.find(t => t.id === depId)
          if (!depTask) {
            issues.push({
              type: 'missing_dependency',
              message: `Task ${task.name} depends on non-existent task ${depId}`,
              tasks: [task]
            })
          } else {
            // Check if dependency ends before this task starts
            const depEnd = new Date(depTask.endTime).getTime()
            const taskStart = new Date(task.startTime).getTime()
            if (depEnd > taskStart) {
              issues.push({
                type: 'invalid_dependency',
                message: `Task ${task.name} starts before dependency ${depTask.name} ends`,
                tasks: [task, depTask]
              })
            }
          }
        })
      }
    })
    
    // Check for insufficient buffer time
    const sortedTasks = [...tasks].sort((a, b) => 
      new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    )
    
    for (let i = 0; i < sortedTasks.length - 1; i++) {
      const buffer = this.calculateBufferTime(sortedTasks[i], sortedTasks[i + 1])
      if (buffer < this.MIN_BUFFER_MINUTES && buffer > 0) {
        issues.push({
          type: 'insufficient_buffer',
          message: `Only ${buffer} minutes between ${sortedTasks[i].name} and ${sortedTasks[i + 1].name}`,
          tasks: [sortedTasks[i], sortedTasks[i + 1]]
        })
      }
    }
    
    return issues
  }
}